import { MenuItem, Stack, TextField } from "@mui/material";

import {
  MCPDetailsForm,
  MCP_TIMEOUT_MAX,
  MCP_TIMEOUT_MIN,
  MCP_TRANSPORT_OPTIONS,
  isValidSlug,
  isValidTimeout,
} from "../mcpResource";

interface MCPServerFieldsProps {
  form: MCPDetailsForm;
  onChange: (field: keyof MCPDetailsForm, value: string | number) => void;
  testIdPrefix: string;
  /** The slug namespaces existing tool names, so it is immutable after creation. */
  slugLocked?: boolean;
  /** Flag an empty slug as an error (once the user has had a chance to fill it). */
  slugRequiredError?: boolean;
}

export default function MCPServerFields({
  form,
  onChange,
  testIdPrefix,
  slugLocked = false,
  slugRequiredError = false,
}: MCPServerFieldsProps) {
  const slugEmpty = form.slug === "";
  const timeoutError = !isValidTimeout(form.timeout_seconds);
  const slugError =
    !slugLocked && (slugEmpty ? slugRequiredError : !isValidSlug(form.slug));
  return (
    <Stack direction="column" gap={2}>
      <TextField
        label="Slug"
        value={form.slug}
        onChange={(e) => onChange("slug", e.target.value)}
        fullWidth
        required
        disabled={slugLocked}
        error={slugError}
        helperText={
          slugLocked
            ? `Tools are exposed as mcp_${form.slug}__<tool>`
            : slugError
              ? slugEmpty
                ? "A slug is required"
                : "Lowercase letters, digits and hyphens only"
              : `Tools will be exposed as mcp_${form.slug || "<slug>"}__<tool>`
        }
        inputProps={{ "data-testid": `${testIdPrefix}-input-mcp-slug` }}
      />
      <Stack direction="row" gap={2}>
        <TextField
          label="Auth Header"
          value={form.auth_header}
          onChange={(e) => onChange("auth_header", e.target.value)}
          fullWidth
          helperText="Header the key is sent in"
          inputProps={{
            "data-testid": `${testIdPrefix}-input-mcp-auth-header`,
          }}
        />
        <TextField
          select
          label="Auth Scheme"
          value={form.auth_scheme}
          onChange={(e) => onChange("auth_scheme", e.target.value)}
          fullWidth
          helperText="Prefix before the key"
          data-testid={`${testIdPrefix}-input-mcp-auth-scheme`}
        >
          <MenuItem value="Bearer">Bearer</MenuItem>
          <MenuItem value="">None (raw key)</MenuItem>
        </TextField>
        <TextField
          label="Timeout (s)"
          type="number"
          value={form.timeout_seconds}
          onChange={(e) =>
            onChange(
              "timeout_seconds",
              e.target.value === "" ? "" : Number(e.target.value),
            )
          }
          fullWidth
          required
          error={timeoutError}
          helperText={
            timeoutError
              ? `Whole seconds, ${MCP_TIMEOUT_MIN} to ${MCP_TIMEOUT_MAX}`
              : "Per tool call"
          }
          inputProps={{
            min: MCP_TIMEOUT_MIN,
            max: MCP_TIMEOUT_MAX,
            "data-testid": `${testIdPrefix}-input-mcp-timeout`,
          }}
        />
      </Stack>
      <TextField
        select
        label="Transport"
        value={form.transport}
        onChange={(e) => onChange("transport", e.target.value)}
        fullWidth
        helperText={
          form.transport === "auto"
            ? "Tries streamable HTTP, falls back to SSE if the server refuses it"
            : form.transport === "sse"
              ? "URL should be the server's SSE stream endpoint, e.g. http://host:8010/sse"
              : "URL should be the streamable HTTP endpoint, e.g. http://host:8097/mcp"
        }
        data-testid={`${testIdPrefix}-input-mcp-transport`}
      >
        {MCP_TRANSPORT_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}
